<script setup lang="ts">
import { ref, computed } from "vue";
import ArticlesTableHeader from './ArticlesTableHeader.vue';
import ArticlesTableRow from './ArticlesTableRow.vue';
import ArticlesTableFooter from './ArticlesTableFooter.vue';
import { formatCurrency } from "src/utils/currency";
import type { FetchArticleResponse, AddArticlesToTicketType } from "src/api/models";

// Extend locally until the global type is fixed
interface ExtendedArticle extends FetchArticleResponse {
  quantity?: number;
  association_id?: string;
  type?: AddArticlesToTicketType;
  price?: number | null;
}

const props = defineProps<{
  articles: ExtendedArticle[];
  loading: boolean;
  totalArticleValue: number;
}>();

const emit = defineEmits<{
  (e: 'update-quantity', associationId: string, quantity: number): void;
  (e: 'update-price', associationId: string, price: number): void;
  (e: 'remove-article', article: ExtendedArticle, event: Event): void;
}>();

// Track which articles are currently being updated (by association_id)
const updatingArticles = ref<Set<string>>(new Set());

const formattedTotalValue = computed(() => {
  return formatCurrency(props.totalArticleValue);
});

function isArticleUpdating(associationId?: string): boolean {
  return associationId ? updatingArticles.value.has(associationId) : false;
}

function handleQuantityUpdate(associationId: string, quantity: number) {
  emit('update-quantity', associationId, quantity);
}

function handlePriceUpdate(associationId: string, price: number) {
  emit('update-price', associationId, price);
}

function handleRemoveArticle(article: ExtendedArticle, event: Event) {
  emit('remove-article', article, event);
}

// Expose methods for parent to manage loading state
defineExpose({
  setUpdating: (associationId: string, updating: boolean) => {
    if (updating) {
      updatingArticles.value.add(associationId);
    } else {
      updatingArticles.value.delete(associationId);
    }
  },
  clearUpdating: () => {
    updatingArticles.value.clear();
  }
});
</script>

<template>
  <div class="border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
    <!-- Table Body: Loading State -->
    <div v-if="loading" class="p-4 text-center">
      <i class="pi pi-spin pi-spinner mr-2 text-blue-500"></i>
      <span>Loading articles...</span>
    </div>

    <!-- Table Body: Content -->
    <div v-else-if="articles.length > 0" class="max-h-[60vh] overflow-x-auto">
      <div class="table w-full">
        <!-- Table Header -->
        <ArticlesTableHeader />

        <!-- Table Rows -->
        <ArticlesTableRow
          v-for="article in articles"
          :key="article.id"
          :article="article"
          :is-updating="isArticleUpdating(article.association_id)"
          @update-quantity="handleQuantityUpdate"
          @update-price="handlePriceUpdate"
          @remove="handleRemoveArticle"
        />
      </div>

      <!-- Table Footer with Totals -->
      <ArticlesTableFooter
        :total-items="articles.length"
        :total-value="formattedTotalValue"
      />
    </div>

    <!-- Empty State -->
    <div v-else class="text-center p-6 bg-gray-50 dark:bg-gray-800/50">
      <i class="pi pi-box text-4xl text-gray-400 dark:text-gray-500 mb-3"></i>
      <p class="text-gray-700 dark:text-gray-300">No articles found for this ticket</p>
      <p class="text-gray-500 text-sm mt-2">Add articles to start tracking items</p>
    </div>
  </div>
</template>
